import { useState, useEffect, useCallback, useMemo } from "react";
import DataTable from "../widget/DataTable";
import { DataTableColumn } from "../widget/DataTable";
import {UserRegistration} from '../../models/UserRegistration';
import Button from "react-bootstrap/esm/Button";
import { Trash } from "react-bootstrap-icons";
import { DeleteRegistrationModal } from "./modal/DeleteRegistrationModal";
import { getData } from "../../utils/GetData";
import { sharedSearchCache } from "../../../api/SearchCache";
import UserRegistrationEndpoint from "../../../api/endpoint/UserRegistrationEndpoint";

export const UserRegistrationList: React.FC<{courseEventId: number, initializeFetch: boolean, initiateReloadInParent: () => void}> = ({courseEventId, initializeFetch, initiateReloadInParent}) => {
    const [users, setUsers] = useState<UserRegistration[] | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ column: string | null; direction: "asc" | "desc" | "none" }>({ column: null, direction: "asc" });
    const [pageSize, setPageSize] = useState(5);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [needsReload, setNeedsReload] = useState(false);

    const [showDelete, setShowDelete] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserRegistration | undefined>(undefined);
    const [reloadKey, setReloadKey] = useState(0);

    const getUsers = async () => {
        const result = await getData<UserRegistration>(
            'api/registration/search',
            page,
            pageSize,
            {
                column: sort.column,
                direction: sort.direction,
                parameters: {'course_event_id': courseEventId}
            }
        );

        if(typeof result === 'object') {
            const registrationsResult: UserRegistration[] = result.data.map((row: any) => ({
                user_registration_id: Number(row.user_registration_id),
                course_event_id: Number(row.course_event_id),
                full_name: row.full_name,
                email: row.email,
                phone_number: row.phone_number,
                attended_class_1: row.attended_class_1,
                attended_class_2: row.attended_class_2,
                survey_completed: row.survey_completed
            }));
            setUsers(registrationsResult);
            setTotal(result.total);
            setSearchError(null);
        } else {
            setSearchError(result);
        }
    };

    const updateAttendance1 = async(user_registration_id: number, attended: boolean) => {
        const result = await UserRegistrationEndpoint.Update({
            user_registration_id: user_registration_id,
            attended_class_1: attended
        });

        if(!result || !result.data || result.errors.length > 0){
            console.error('Error updating course');
        } else {
            initiateReload();
        }
    };

    const updateAttendance2 = async(user_registration_id: number, attended: boolean) => {
        const result = await UserRegistrationEndpoint.Update({
            user_registration_id: user_registration_id,
            attended_class_2: attended
        });

        if(!result || !result.data || result.errors.length > 0){
            console.error('Error updating course');
        } else {
            initiateReload();
        }
    };

    useEffect(() => {
        if(initializeFetch){
            initiateReload();
        }
    }, [page, sort, pageSize, initializeFetch]);

    useEffect(() => {
        if(needsReload) {
            getUsers();
            setNeedsReload(false);
            setReloadKey(prev => prev + 1);
        }
    }, [needsReload]);
    
    const initiateReload = useCallback(() => {
        sharedSearchCache.clear();
        setTimeout(() => {
            setNeedsReload(true);
        }, 1);
    }, [setNeedsReload]);

    const cols = useMemo<DataTableColumn<UserRegistration>[]>(() => [
        {column: "full_name", label: "Full Name", sortable:true, headerClassName:"sortable-header"},
        {column: "email", label: "Email", sortable:true, headerClassName:"sortable-header"},
        {column: "phone_number", label: "Phone Number", sortable:true, headerClassName:"sortable-header"},
        {column: "attended_event", label: "Attended Event?", 
            accessor: (row:UserRegistration) => {
                return(
                    <>
                        {row.attended_class_1 && row.attended_class_2 ?
                            <div className="text-center">Yes</div>
                            :
                            <div className="text-center">No</div>
                        }
                    </>
                );
            }
        },
        {column: "survey_completed", label: "Survey Completed?", headerClassName:'text-center sortable-header', sortable:true,
            accessor: (row:UserRegistration) => {
                return (
                    <>
                        {row.survey_completed?
                            <div className="text-center">Yes</div>
                            :
                            <div className="text-center">No</div>
                        }
                    </>
                );
            }
        },
        {column: "attended_class_1", label: "Update Class 1 Attendance", headerClassName:'text-center sortable-header', sortable: true, 
            accessor: (row:UserRegistration) => {
                return(
                    <div className="d-flex justify-content-center">
                    {row.attended_class_1?
                        <Button variant="warning" style={{width:'12em'}} onClick={() => {updateAttendance1(row.user_registration_id, false)}}>Mark Not Attended</Button>
                        :
                        <Button variant="success" style={{width:'12em'}} onClick={() => {updateAttendance1(row.user_registration_id, true)}}>Mark Attended</Button>
                    }
                    </div>
                );
            }
        },
        {column: "attended_class_2", label: "Update Class 2 Attendance", headerClassName:'text-center sortable-header', sortable:true,
            accessor: (row:UserRegistration) => {
                return(
                    <div className="d-flex justify-content-center">
                    {row.attended_class_2?
                        <Button variant="warning" style={{width:'12em'}} onClick={() => {updateAttendance2(row.user_registration_id, false)}}>Mark Not Attended</Button>
                        :
                        <Button variant="success" style={{width:'12em'}} onClick={() => {updateAttendance2(row.user_registration_id, true)}}>Mark Attended</Button>
                    }
                    </div>
                );
            }
        },
        {column: "delete_registration", label: "Delete Registration", headerClassName:"text-center",
            accessor: (row:UserRegistration) => (
                <div className="d-flex justify-content-center">
                    <Button 
                        className="text-light" 
                        variant="danger"
                        onClick={() => {
                            setSelectedUser(row)
                            setShowDelete(true);
                        }}
                        aria-label="Delete"
                    >
                        <Trash/>
                    </Button>
                </div>
            )
        }
    ], [users]);

    const handleModalClose = () => {
        setShowDelete(false);
        setSelectedUser(undefined);
        initiateReloadInParent();
    };

    const handleSort = (col: DataTableColumn<UserRegistration>) => {
        if (!col.sortable) return;
        let direction: "asc" | "desc" | "none" = "asc";
        if (sort.column === col.column) {
            if (sort.direction === "asc") {
                direction = "desc";
            } else if (sort.direction === "desc") {
                direction = "none";
            } else {
                direction = "asc";
            }
        }
        const newSort = { column: direction === "none" ? null : col.column, direction };
        setSort(newSort);
        setPage(1);
        initiateReload();
    };

    return (
        <>
            {selectedUser &&
                <DeleteRegistrationModal user={selectedUser} onHide={handleModalClose} show={showDelete} 
                    initiateReload={() => {
                        if(users?.length == 1 && page > 1) setPage(page - 1);
                        initiateReload();
                    }}
                />
            }
            {searchError &&
                <div>
                    <strong>Search error:</strong> {searchError}
                </div>
            }
            {users && users.length > 0 &&
                <div className="user-registration-table row justify-content-center" style={{overflowX:'auto'}}>
                    <DataTable
                        columns={cols}
                        key={reloadKey}
                        data={users}
                        page={page}
                        pageSize={pageSize}
                        totalRecords={total}
                        onPageChange={setPage}
                        id={`${courseEventId}-users-list`}
                        handlePageSizeChange={(newPageSize: number) => {
                            setPageSize(newPageSize);
                            setPage(1);  // Reset to first page when changing items per page
                        }}
                        sort={sort}
                        onSort={handleSort}
                    />
                </div>
            }
            {!users || users.length === 0 &&
                <div>
                    No users are currently registered for this event.
                </div>
            }
        </>
        
    )
}